import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) { setCart([]); return; }
    try {
      setLoading(true);
      const { data } = await api.get("/cart");
      setCart((data.items || []).map(i => ({
        ...(i.product || {}),
        quantity: i.qty || 1,
        variant: i.variant,
        product_id: i.product?.id || i.product?._id
      })));
    } catch { setCart([]); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (user) fetchCart(); else setCart([]); }, [user, fetchCart]);

  const addToCart = useCallback(async (productId, qty = 1, variant = null) => {
    const body = { product_id: productId, qty: qty };
    if (variant) body.variant = variant;
    await api.post("/cart", body);
    await fetchCart();
  }, [fetchCart]);

  const updateQty = useCallback(async (itemId, qty) => {
    await api.put("/cart", { product_id: itemId, qty: qty });
    await fetchCart();
  }, [fetchCart]);

  const removeItem = useCallback(async (itemId) => {
    await api.delete(`/cart/${itemId}`);
    await fetchCart();
  }, [fetchCart]);

  const clearCart = useCallback(async () => {
    await api.delete("/cart/clear");
    setCart([]);
  }, []);

  const totalItems = cart.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const totalPrice = cart.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0);

  return (
    <CartContext.Provider value={{ cart, loading, fetchCart, addToCart, updateQty, removeItem, clearCart, totalItems, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
}
